// Sparky PWA entry point (bundled by build.mjs -> ui/public/app.js).
//
// This file owns the auth gate (task 3.4): a Cognito login screen and sign-out.
// Once signed in it hands off to the mobile-first app shell (task 3.5, shell.js),
// which owns the bottom nav, home screen, and routing. Everything under the auth
// gate calls the API only through ./api.js so the ID token is always attached
// (Req 1.3) and a 401/expiry always routes back here (Req 1.5).

import { isConfigured, getConfigError } from './config.js';
import * as auth from './auth.js';
import { AuthStatus } from './auth.js';
import { setUnauthorizedHandler } from './api.js';
import { initOfflineSync } from './offlineSync.js';
import { h, clear } from './lib/dom.js';
import { mountShell } from './shell.js';
import { parseQuickBooksReturn, setQuickBooksReturnStatus } from './screens/quickbooks.js';

const root = () => document.getElementById('app');

// --- Screens ---------------------------------------------------------------

function renderNotConfigured() {
  const app = root();
  clear(app);
  app.appendChild(
    h('section', { class: 'auth-card', role: 'alert' }, [
      h('h1', { class: 'auth-title', text: 'Sparky' }),
      h('p', { class: 'auth-error' }, ['App not configured.']),
      h('p', { class: 'auth-hint' }, [getConfigError() || '']),
    ]),
  );
}

// Shared card scaffold with a title, an optional notice/error line, and a form.
function renderCard({ title, notice, error, form }) {
  const app = root();
  clear(app);
  const children = [h('h1', { class: 'auth-title', text: title })];
  if (notice) children.push(h('p', { class: 'auth-notice' }, [notice]));
  if (error) children.push(h('p', { class: 'auth-error', role: 'alert' }, [error]));
  children.push(form);
  app.appendChild(h('section', { class: 'auth-card' }, children));
}

function busy(button, isBusy, idleLabel) {
  button.disabled = isBusy;
  button.textContent = isBusy ? 'Please wait…' : idleLabel;
}

function renderLogin({ notice, error, email = '' } = {}) {
  const emailInput = h('input', {
    class: 'auth-input', type: 'email', name: 'email', autocomplete: 'username',
    inputmode: 'email', placeholder: 'you@example.co.uk', required: true, value: email,
  });
  const pwInput = h('input', {
    class: 'auth-input', type: 'password', name: 'password',
    autocomplete: 'current-password', placeholder: 'Password', required: true,
  });
  const submit = h('button', { class: 'auth-btn', type: 'submit', text: 'Sign in' });

  const form = h('form', {
    class: 'auth-form', novalidate: true,
    onsubmit: async (e) => {
      e.preventDefault();
      busy(submit, true, 'Sign in');
      try {
        const result = await auth.signIn(emailInput.value, pwInput.value);
        routeAfterAuth(result, emailInput.value);
      } catch (err) {
        renderLogin({ email: emailInput.value, error: friendlyAuthError(err) });
      }
    },
  }, [
    h('label', { class: 'auth-label', text: 'Email' }), emailInput,
    h('label', { class: 'auth-label', text: 'Password' }), pwInput,
    submit,
  ]);

  renderCard({ title: 'Sign in to Sparky', notice, error, form });
}

// AdminCreateUser accounts sign in first with a temporary password and must set a
// permanent one (Req 1 — single provisioned user, no public sign-up).
function renderNewPassword(email) {
  const pwInput = h('input', {
    class: 'auth-input', type: 'password', name: 'newPassword',
    autocomplete: 'new-password', placeholder: 'New password', required: true, minlength: '12',
  });
  const submit = h('button', { class: 'auth-btn', type: 'submit', text: 'Set password' });
  const form = h('form', {
    class: 'auth-form', novalidate: true,
    onsubmit: async (e) => {
      e.preventDefault();
      busy(submit, true, 'Set password');
      try {
        const result = await auth.completeNewPassword(pwInput.value);
        routeAfterAuth(result, email);
      } catch (err) {
        busy(submit, false, 'Set password');
        renderNewPasswordError(email, friendlyAuthError(err));
      }
    },
  }, [
    h('label', { class: 'auth-label', text: 'Choose a password (min 12 characters)' }),
    pwInput, submit,
  ]);
  renderCard({
    title: 'Set your password',
    notice: 'First sign-in: choose a permanent password.',
    form,
  });
}

function renderNewPasswordError(email, error) {
  renderNewPassword(email);
  const card = root().querySelector('.auth-card');
  if (card) card.insertBefore(h('p', { class: 'auth-error', role: 'alert' }, [error]), card.querySelector('form'));
}

// Optional TOTP MFA challenge (Req 1.4).
function renderTotp(email) {
  const codeInput = h('input', {
    class: 'auth-input', type: 'text', name: 'code', inputmode: 'numeric',
    autocomplete: 'one-time-code', placeholder: '123456', required: true, pattern: '[0-9]*',
  });
  const submit = h('button', { class: 'auth-btn', type: 'submit', text: 'Verify' });
  const form = h('form', {
    class: 'auth-form', novalidate: true,
    onsubmit: async (e) => {
      e.preventDefault();
      busy(submit, true, 'Verify');
      try {
        const result = await auth.confirmTotp(codeInput.value);
        routeAfterAuth(result, email);
      } catch (err) {
        busy(submit, false, 'Verify');
        renderCard({
          title: 'Two-factor code',
          error: friendlyAuthError(err),
          form,
        });
      }
    },
  }, [
    h('label', { class: 'auth-label', text: 'Authenticator code' }),
    codeInput, submit,
  ]);
  renderCard({ title: 'Two-factor code', notice: 'Enter the 6-digit code from your authenticator app.', form });
}

// Authenticated app: mount the mobile-first shell (bottom nav, home, routing —
// task 3.5). Sign-out clears the Cognito session and returns to the login screen.
function renderApp() {
  // The QuickBooks OAuth callback redirects the browser back to
  // /settings?quickbooks=connected|error (it can't carry a hash). Capture that
  // outcome for a Settings banner, strip the query so a refresh doesn't repeat it,
  // and route to Settings so the banner is seen (task 5.4).
  handleQuickBooksReturn();
  // Drain any job captures queued offline (this session or a previous one) and
  // keep flushing on every reconnect (task 3.7, Req 3.6). Idempotent.
  initOfflineSync();
  mountShell(root(), {
    onSignOut: () => {
      auth.signOut();
      renderLogin({ notice: 'Signed out.' });
    },
  });
}

// If we've just returned from the QuickBooks OAuth consent screen, remember the
// outcome for the Settings banner, replace the URL with the Settings hash route
// (dropping the ?quickbooks=… query), and let the router render Settings.
function handleQuickBooksReturn() {
  const status = parseQuickBooksReturn(window.location.search);
  if (!status) return;
  setQuickBooksReturnStatus(status);
  window.history.replaceState(null, '', '/#/settings');
}

// --- Flow control ----------------------------------------------------------

function routeAfterAuth(result, email) {
  switch (result.status) {
    case AuthStatus.NEW_PASSWORD_REQUIRED:
      renderNewPassword(email);
      break;
    case AuthStatus.SOFTWARE_TOKEN_MFA:
      renderTotp(email);
      break;
    case AuthStatus.SUCCESS:
    default:
      renderApp();
      break;
  }
}

function friendlyAuthError(err) {
  const code = err && (err.code || err.name);
  const message = (err && err.message) || 'Something went wrong.';
  switch (code) {
    case 'NotAuthorizedException':
      return 'Incorrect email or password.';
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'CodeMismatchException':
      return 'That code is not valid. Try again.';
    case 'InvalidPasswordException':
      return 'Password does not meet the requirements (min 12 chars, upper, lower, number, symbol).';
    case 'PasswordResetRequiredException':
      return 'Password reset required — contact the administrator.';
    default:
      return message;
  }
}

// On load: gate on config, then on an existing valid session. getValidIdToken()
// refreshes silently if needed and throws if the session is gone (Req 1.5).
async function boot() {
  // Any 401 / lost session anywhere in the app routes back to login.
  setUnauthorizedHandler(() => renderLogin({ notice: 'Your session ended — please sign in again.' }));

  if (!isConfigured()) {
    renderNotConfigured();
    return;
  }
  if (auth.hasCurrentUser()) {
    try {
      await auth.getValidIdToken();
      renderApp();
      return;
    } catch {
      auth.signOut();
    }
  }
  renderLogin();
}

boot();
